import { useEffect, useState } from "react";
import { Loader2, IndianRupee, ShoppingBag, Users, Package, GraduationCap, Star, MessageSquareWarning, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatCard, StatusBadge, LoadingRows, TableShell, TR, TD, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";

export default function RootMarketplace() {
  const [ov, setOv] = useState(null);
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState(null);
  const [orderFilter, setOrderFilter] = useState("pending_payment");
  const [subs, setSubs] = useState(null);
  const [withdrawals, setWithdrawals] = useState(null);
  const [freelancers, setFreelancers] = useState(null);
  const [listings, setListings] = useState(null);
  const [listKind, setListKind] = useState("service");
  const [reviews, setReviews] = useState(null);
  const [cats, setCats] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [flagged, setFlagged] = useState(null);
  const [apps, setApps] = useState(null);
  const [appAction, setAppAction] = useState(null); // {app, mode: 'reject'|'info'}
  const [appNote, setAppNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [refundFor, setRefundFor] = useState(null);
  const [refundNote, setRefundNote] = useState("");
  const [payRef, setPayRef] = useState({});

  const loadOv = () => api.get("/root/mkt/overview").then((r) => setOv(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadSettings = () => api.get("/root/mkt/settings").then((r) => setSettings(r.data)).catch(() => {});
  const loadOrders = (st = orderFilter) => api.get("/root/mkt/orders", { params: st ? { status: st } : {} }).then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
  const loadSubs = () => api.get("/root/mkt/subscriptions").then((r) => setSubs(r.data.subscriptions)).catch(() => setSubs([]));
  const loadWd = () => api.get("/root/mkt/withdrawals").then((r) => setWithdrawals(r.data.withdrawals)).catch(() => setWithdrawals([]));
  const loadFl = () => api.get("/root/mkt/freelancers").then((r) => setFreelancers(r.data.freelancers)).catch(() => setFreelancers([]));
  const loadListings = (k = listKind) => api.get("/root/mkt/listings", { params: { kind: k } }).then((r) => setListings(r.data.items)).catch(() => setListings([]));
  const loadReviews = () => api.get("/root/mkt/reviews").then((r) => setReviews(r.data.reviews)).catch(() => setReviews([]));
  const loadCats = () => api.get("/root/mkt/categories").then((r) => setCats(r.data.categories)).catch(() => setCats([]));
  const loadFlagged = () => api.get("/root/mkt/flagged").then((r) => setFlagged(r.data.flagged)).catch(() => setFlagged([]));
  const loadApps = () => api.get("/root/mkt/applications").then((r) => setApps(r.data.applications)).catch(() => setApps([]));

  useEffect(() => {
    loadOv(); loadSettings(); loadOrders("pending_payment"); loadSubs(); loadWd(); loadFl(); loadListings("service"); loadReviews(); loadCats(); loadFlagged(); loadApps();
  }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveSettings = async () => act(async () => {
    const { data } = await api.put("/root/mkt/settings", {
      commission_pct: Number(settings.commission_pct), subscription_price: Number(settings.subscription_price),
      min_withdrawal: Number(settings.min_withdrawal), payment_link: settings.payment_link,
      auto_complete_days: Number(settings.auto_complete_days),
      subscription_required_to_publish: !!settings.subscription_required_to_publish, weights: settings.weights,
    });
    setSettings(data);
  }, "Marketplace settings saved");

  return (
    <div className="space-y-2" data-testid="root-marketplace-page">
      <PageHeader title="Marketplace Control" subtitle="Freelancer ecosystem — payments, payouts, moderation, settings" testid="root-mkt-title" />

      <Tabs defaultValue="overview">
        <TabsList data-testid="root-mkt-tabs" className="flex-wrap h-auto">
          {["overview", "applications", "orders", "subscriptions", "withdrawals", "freelancers", "listings", "reviews", "categories", "settings", "flagged"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`root-mkt-tab-${t}`} className="capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-6">
          {!ov ? <LoadingRows rows={3} /> : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={IndianRupee} label="GMV" value={ov.gmv} testid="ov-gmv" tint="bg-emerald-500/10 text-emerald-500" />
              <StatCard icon={IndianRupee} label="Commission Revenue" value={ov.commission_revenue} testid="ov-commission" delay={0.05} tint="bg-blue-500/10 text-blue-500" />
              <StatCard icon={IndianRupee} label="Subscription Revenue" value={ov.subscription_revenue} testid="ov-subscriptions" delay={0.1} tint="bg-violet-500/10 text-violet-500" />
              <StatCard icon={ShoppingBag} label="Paid Orders" value={ov.orders} testid="ov-orders" delay={0.15} />
              <StatCard icon={Users} label="Freelancers" value={ov.freelancers} testid="ov-freelancers" delay={0.2} tint="bg-amber-500/10 text-amber-500" />
              <StatCard icon={Package} label="Listings (S/P/C)" value={ov.services + ov.products + ov.courses} testid="ov-listings" delay={0.25} />
              <StatCard icon={IndianRupee} label="Avg Order Value" value={ov.aov} testid="ov-aov" delay={0.3} tint="bg-cyan-500/10 text-cyan-500" />
              <StatCard icon={MessageSquareWarning} label="Flagged Messages" value={ov.flagged_messages} testid="ov-flagged" delay={0.35} tint="bg-red-500/10 text-red-500" />
            </div>
          )}
        </TabsContent>

        {/* APPLICATIONS */}
        <TabsContent value="applications" className="mt-6">
          {!apps ? <LoadingRows rows={3} /> : apps.length === 0 ? <EmptyState title="No freelancer applications" body="New freelancer applications with payment details appear here." /> : (
            <TableShell head={["Applicant", "Profile", "Payment", "Status", "Actions"]} testid="root-mkt-applications">
              {apps.map((a) => (
                <TR key={a.user_id} testid={`root-app-${a.user_id}`}>
                  <TD>
                    <p className="font-semibold text-sm">{a.name}</p>
                    <p className="text-[10px] text-muted-foreground">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
                    <p className="text-[10px] text-muted-foreground">{a.role} · applied {a.applied_at ? fmtDateTime(a.applied_at) : "—"}</p>
                  </TD>
                  <TD>
                    <p className="text-xs font-semibold">{a.profile.display_name} — {a.profile.title}</p>
                    <p className="text-[10px] text-muted-foreground max-w-[220px] truncate">{(a.profile.skills || []).join(", ")}</p>
                    {(a.profile.portfolio || []).length > 0 && (
                      <a href={a.profile.portfolio[0]} target="_blank" rel="noreferrer" className="text-[10px] text-primary font-semibold">Portfolio ↗</a>)}
                  </TD>
                  <TD>
                    {a.payment ? (
                      <span className="text-xs">
                        <b>{fmtINR(a.payment.amount)}</b> · ref {a.payment.reference}
                        {a.payment.payment_date && <span className="text-muted-foreground"> · {a.payment.payment_date}</span>}
                        {a.payment.screenshot && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${a.payment.screenshot}`} target="_blank" rel="noreferrer"
                            data-testid={`app-screenshot-${a.user_id}`} className="block text-primary font-semibold text-[10px]">Screenshot ↗</a>)}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">No payment yet</span>}
                  </TD>
                  <TD><StatusBadge status={a.status} />{a.rejection_reason && <p className="text-[10px] text-red-500 mt-1 max-w-[160px]">{a.rejection_reason}</p>}</TD>
                  <TD>
                    {["draft", "pending", "info_requested"].includes(a.status) && (
                      <div className="flex gap-1.5 flex-wrap">
                        <Button size="sm" data-testid={`app-approve-${a.user_id}`} disabled={busy}
                          onClick={() => act(async () => { await api.post(`/root/mkt/applications/${a.user_id}/approve`); loadApps(); loadFl(); loadOv(); }, "Application approved — freelancer active")}
                          className="rounded-full font-bold gradient-bg text-white border-0">Approve</Button>
                        <Button size="sm" variant="outline" data-testid={`app-reject-${a.user_id}`} onClick={() => { setAppAction({ app: a, mode: "reject" }); setAppNote(""); }} className="rounded-full font-bold">Reject</Button>
                        <Button size="sm" variant="ghost" data-testid={`app-info-${a.user_id}`} onClick={() => { setAppAction({ app: a, mode: "info" }); setAppNote(""); }} className="rounded-full font-bold">Request Info</Button>
                      </div>)}
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-6 space-y-4">
          <div className="flex items-center gap-3">
            <Select value={orderFilter} onValueChange={(v) => { setOrderFilter(v); setOrders(null); loadOrders(v); }}>
              <SelectTrigger data-testid="root-mkt-order-filter" className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[["pending_payment", "Pending Verification"], ["active", "Active"], ["delivered", "Delivered"], ["completed", "Completed"], ["refunded", "Refunded"], ["", "All"]].map(([v, l]) => (
                  <SelectItem key={v || "all"} value={v || "all"}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {!orders ? <LoadingRows rows={3} /> : orders.length === 0 ? <EmptyState title="No orders" body="Nothing in this state right now." /> : (
            <TableShell head={["Order", "Buyer", "Seller", "Amount", "Status", "Actions"]} testid="root-mkt-orders">
              {orders.map((o) => (
                <TR key={o.id} testid={`root-mkt-order-${o.id}`}>
                  <TD><p className="font-semibold text-sm max-w-[220px] truncate">{o.item_title}</p><p className="text-[10px] text-muted-foreground">{o.kind} · #{o.id.slice(0, 8)}</p></TD>
                  <TD><span className="text-xs">{o.buyer_name}<br /><span className="text-muted-foreground">{o.buyer_email}</span></span></TD>
                  <TD><span className="text-xs">{o.freelancer_name}</span></TD>
                  <TD><span className="font-bold">{fmtINR(o.amount)}</span>{o.payment_reference && <p className="text-[10px] text-muted-foreground">ref: {o.payment_reference}</p>}</TD>
                  <TD><StatusBadge status={o.status} /></TD>
                  <TD>
                    <div className="flex gap-2">
                      {o.status === "pending_payment" && o.payment_reference && (
                        <Button size="sm" data-testid={`verify-pay-${o.id}`} disabled={busy}
                          onClick={() => act(async () => { await api.post(`/root/mkt/orders/${o.id}/verify-payment`); loadOrders(); loadOv(); }, "Payment verified — order active")}
                          className="rounded-full font-bold gradient-bg text-white border-0">Verify</Button>)}
                      {!["refunded", "cancelled"].includes(o.status) && (
                        <Button size="sm" variant="outline" data-testid={`refund-${o.id}`} onClick={() => setRefundFor(o)} className="rounded-full font-bold">Refund</Button>)}
                    </div>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* SUBSCRIPTIONS */}
        <TabsContent value="subscriptions" className="mt-6">
          {!subs ? <LoadingRows rows={3} /> : subs.length === 0 ? <EmptyState title="No pending subscriptions" body="Freelancer subscription payments awaiting verification appear here." /> : (
            <TableShell head={["Freelancer", "Amount", "Reference", "Submitted", "Actions"]} testid="root-mkt-subs">
              {subs.map((s) => (
                <TR key={s.id} testid={`root-mkt-sub-${s.id}`}>
                  <TD><p className="font-semibold text-sm">{s.user_name}</p><p className="text-[10px] text-muted-foreground">{s.user_email}</p></TD>
                  <TD><span className="font-bold">{fmtINR(s.amount)}</span></TD>
                  <TD><span className="text-xs">{s.reference}</span></TD>
                  <TD><span className="text-xs text-muted-foreground">{fmtDateTime(s.created_at)}</span></TD>
                  <TD>
                    <div className="flex gap-2">
                      <Button size="sm" data-testid={`sub-approve-${s.id}`} disabled={busy}
                        onClick={() => act(async () => { await api.post(`/root/mkt/subscriptions/${s.id}/approve`); loadSubs(); loadOv(); }, "Subscription approved (+30 days)")}
                        className="rounded-full font-bold gradient-bg text-white border-0">Approve</Button>
                      <Button size="sm" variant="outline" data-testid={`sub-reject-${s.id}`} disabled={busy}
                        onClick={() => act(async () => { await api.post(`/root/mkt/subscriptions/${s.id}/reject`, { note: "Payment not found" }); loadSubs(); }, "Rejected")}
                        className="rounded-full font-bold">Reject</Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* WITHDRAWALS */}
        <TabsContent value="withdrawals" className="mt-6">
          {!withdrawals ? <LoadingRows rows={3} /> : withdrawals.length === 0 ? <EmptyState title="No withdrawal requests" body="Freelancer payout requests appear here." /> : (
            <TableShell head={["Freelancer", "Amount", "Payout Details", "Status", "Action"]} testid="root-mkt-withdrawals">
              {withdrawals.map((w) => (
                <TR key={w.id} testid={`root-mkt-wd-${w.id}`}>
                  <TD><p className="font-semibold text-sm">{w.freelancer_name}</p><p className="text-[10px] text-muted-foreground">{w.freelancer_email}</p></TD>
                  <TD><span className="font-bold">{fmtINR(w.amount)}</span></TD>
                  <TD><span className="text-xs">{w.payout_details?.upi_id || ""}{w.payout_details?.account_number ? `A/C ${w.payout_details.account_number} ${w.payout_details.ifsc || ""}` : ""}{!w.payout_details?.upi_id && !w.payout_details?.account_number ? "—" : ""}</span></TD>
                  <TD><StatusBadge status={w.status} />{w.reference && <p className="text-[10px] text-muted-foreground mt-1">{w.reference}</p>}</TD>
                  <TD>
                    {w.status === "pending" && (
                      <div className="flex gap-2 items-center">
                        <Input placeholder="UTR / ref" value={payRef[w.id] || ""} onChange={(e) => setPayRef({ ...payRef, [w.id]: e.target.value })}
                          data-testid={`wd-ref-${w.id}`} className="h-8 w-28 text-xs" />
                        <Button size="sm" data-testid={`wd-paid-${w.id}`} disabled={busy}
                          onClick={() => act(async () => { await api.post(`/root/mkt/withdrawals/${w.id}/paid`, { reference: payRef[w.id] || "" }); loadWd(); loadOv(); }, "Marked as paid")}
                          className="rounded-full font-bold gradient-bg text-white border-0">Paid</Button>
                      </div>)}
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* FREELANCERS */}
        <TabsContent value="freelancers" className="mt-6">
          {!freelancers ? <LoadingRows rows={3} /> : freelancers.length === 0 ? <EmptyState title="No freelancers yet" body="Activated freelancer profiles appear here." /> : (
            <TableShell head={["Freelancer", "Rating", "Orders", "Subscription", "Featured"]} testid="root-mkt-freelancers">
              {freelancers.map((f) => (
                <TR key={f.user_id} testid={`root-mkt-fl-${f.user_id}`}>
                  <TD><p className="font-semibold text-sm flex items-center gap-1.5">{f.display_name} {f.verified && <BadgeCheck className="h-3.5 w-3.5 text-sky-500" />}</p><p className="text-[10px] text-muted-foreground">{f.email}</p></TD>
                  <TD><span className="text-xs flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{f.rating || 0} ({f.reviews || 0})</span></TD>
                  <TD><span className="font-bold">{f.completed_orders || 0}</span></TD>
                  <TD><span className="text-xs text-muted-foreground">{f.subscription_until ? fmtDateTime(f.subscription_until) : "—"}</span></TD>
                  <TD>
                    <Switch data-testid={`fl-feature-${f.user_id}`} checked={!!f.featured} disabled={busy}
                      onCheckedChange={(on) => act(async () => { await api.post(`/root/mkt/freelancers/${f.user_id}/feature`, { on }); loadFl(); }, on ? "Featured" : "Unfeatured")} />
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* LISTINGS */}
        <TabsContent value="listings" className="mt-6 space-y-4">
          <Select value={listKind} onValueChange={(v) => { setListKind(v); setListings(null); loadListings(v); }}>
            <SelectTrigger data-testid="root-mkt-listing-kind" className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="service">Services</SelectItem>
              <SelectItem value="product">Products</SelectItem>
              <SelectItem value="course">Courses</SelectItem>
            </SelectContent>
          </Select>
          {!listings ? <LoadingRows rows={3} /> : listings.length === 0 ? <EmptyState title="No listings" body="Nothing listed in this type yet." /> : (
            <TableShell head={["Listing", "Seller", "Price", "Sales", "Status", "Actions"]} testid="root-mkt-listings">
              {listings.map((it) => (
                <TR key={it.id} testid={`root-mkt-listing-${it.id}`}>
                  <TD><p className="font-semibold text-sm max-w-[240px] truncate">{it.title}</p><p className="text-[10px] text-muted-foreground">{it.category}</p></TD>
                  <TD><span className="text-xs">{it.freelancer_name}</span></TD>
                  <TD><span className="font-bold">{fmtINR(it.offer_price || it.price || it.packages?.basic?.price || 0)}</span></TD>
                  <TD><span className="font-bold">{it.sales || 0}</span></TD>
                  <TD><StatusBadge status={it.status} /></TD>
                  <TD>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" data-testid={`listing-moderate-${it.id}`} disabled={busy}
                        onClick={() => act(async () => { await api.post(`/root/mkt/listings/${listKind}/${it.id}/moderate`, { status: it.status === "published" ? "paused" : "published" }); loadListings(); }, "Status updated")}
                        className="rounded-full font-bold">{it.status === "published" ? "Pause" : "Publish"}</Button>
                      <Button size="sm" variant="ghost" data-testid={`listing-feature-${it.id}`} disabled={busy}
                        onClick={() => act(async () => { await api.post(`/root/mkt/featured`, { kind: listKind, id: it.id, on: !it.featured }); loadListings(); }, it.featured ? "Unfeatured" : "Featured")}
                        className={`rounded-full font-bold ${it.featured ? "text-amber-500" : ""}`}>{it.featured ? "★ Featured" : "☆ Feature"}</Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* REVIEWS */}
        <TabsContent value="reviews" className="mt-6">
          {!reviews ? <LoadingRows rows={3} /> : reviews.length === 0 ? <EmptyState title="No reviews yet" body="Buyer reviews appear here." /> : (
            <TableShell head={["Rating", "Review", "Buyer", "Visibility", "Date"]} testid="root-mkt-reviews">
              {reviews.map((r) => (
                <TR key={r.id} testid={`root-mkt-review-${r.id}`}>
                  <TD><span className="font-bold flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{r.rating}</span></TD>
                  <TD><p className="text-xs max-w-[280px] truncate">{r.text || "—"}</p></TD>
                  <TD><span className="text-xs">{r.buyer_name}</span></TD>
                  <TD>
                    <Switch data-testid={`review-hide-${r.id}`} checked={!r.hidden} disabled={busy}
                      onCheckedChange={(visible) => act(async () => { await api.post(`/root/mkt/reviews/${r.id}/hide`, { hidden: !visible }); loadReviews(); }, visible ? "Review visible" : "Review hidden")} />
                  </TD>
                  <TD><span className="text-xs text-muted-foreground">{fmtDateTime(r.created_at)}</span></TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>

        {/* CATEGORIES */}
        <TabsContent value="categories" className="mt-6 space-y-4">
          <div className="flex gap-2 max-w-md">
            <Input data-testid="new-category-name" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" />
            <Button data-testid="add-category-btn" disabled={busy || !newCat.trim()}
              onClick={() => act(async () => { await api.post("/root/mkt/categories", { name: newCat.trim() }); setNewCat(""); loadCats(); }, "Category added")}
              className="rounded-full font-bold gradient-bg text-white border-0 shrink-0">Add</Button>
          </div>
          {!cats ? <LoadingRows rows={3} /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {cats.map((c) => (
                <div key={c.id} data-testid={`root-cat-${c.id}`} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-2">
                  <span className="font-bold text-sm truncate">{c.name}</span>
                  <Switch data-testid={`cat-active-${c.id}`} checked={!!c.active} disabled={busy}
                    onCheckedChange={(on) => act(async () => { await api.patch(`/root/mkt/categories/${c.id}`, { active: on }); loadCats(); }, on ? "Enabled" : "Disabled")} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings" className="mt-6">
          {!settings ? <LoadingRows rows={3} /> : (
            <div className="max-w-2xl space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Platform commission (%)</Label><Input data-testid="set-commission" type="number" min="0" max="90" value={settings.commission_pct} onChange={(e) => setSettings({ ...settings, commission_pct: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Seller subscription (₹/month)</Label><Input data-testid="set-sub-price" type="number" min="0" value={settings.subscription_price} onChange={(e) => setSettings({ ...settings, subscription_price: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Minimum withdrawal (₹)</Label><Input data-testid="set-min-withdrawal" type="number" min="0" value={settings.min_withdrawal} onChange={(e) => setSettings({ ...settings, min_withdrawal: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Auto-complete after (days)</Label><Input data-testid="set-autocomplete" type="number" min="1" value={settings.auto_complete_days} onChange={(e) => setSettings({ ...settings, auto_complete_days: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Marketplace payment link (SuperProfile / UPI page)</Label><Input data-testid="set-payment-link" value={settings.payment_link || ""} onChange={(e) => setSettings({ ...settings, payment_link: e.target.value })} placeholder="https://…" /></div>
              <div className="flex items-center justify-between rounded-xl border border-border p-4">
                <div><p className="font-bold text-sm">Subscription required to publish</p><p className="text-xs text-muted-foreground">Sellers must have an active plan to publish listings</p></div>
                <Switch data-testid="set-sub-required" checked={!!settings.subscription_required_to_publish} onCheckedChange={(v) => setSettings({ ...settings, subscription_required_to_publish: v })} />
              </div>
              <div className="rounded-xl border border-border p-4 space-y-3">
                <p className="font-bold text-sm">Search ranking weights</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(settings.weights || {}).map(([k, v]) => (
                    <div key={k} className="space-y-1"><Label className="text-xs capitalize">{k}</Label>
                      <Input data-testid={`set-weight-${k}`} type="number" step="0.1" value={v}
                        onChange={(e) => setSettings({ ...settings, weights: { ...settings.weights, [k]: e.target.value } })} /></div>
                  ))}
                </div>
              </div>
              <Button data-testid="save-mkt-settings" onClick={saveSettings} disabled={busy} className="rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* FLAGGED */}
        <TabsContent value="flagged" className="mt-6">
          {!flagged ? <LoadingRows rows={3} /> : flagged.length === 0 ? <EmptyState title="No flagged messages" body="Chat attempts containing contact/payment info appear here." /> : (
            <TableShell head={["User", "Message", "Reason", "Date"]} testid="root-mkt-flagged">
              {flagged.map((f) => (
                <TR key={f.id} testid={`flagged-${f.id}`}>
                  <TD><span className="text-xs font-mono">{f.user_id?.slice(0, 8)}</span></TD>
                  <TD><p className="text-xs max-w-[320px]">{f.text}</p></TD>
                  <TD><span className="text-xs text-red-500 font-semibold">{f.reason}</span></TD>
                  <TD><span className="text-xs text-muted-foreground">{fmtDateTime(f.created_at)}</span></TD>
                </TR>
              ))}
            </TableShell>
          )}
        </TabsContent>
      </Tabs>

      {/* APPLICATION ACTION DIALOG */}
      <Dialog open={!!appAction} onOpenChange={(o) => !o && setAppAction(null)}>
        <DialogContent data-testid="app-action-dialog">
          <DialogHeader><DialogTitle className="font-display">{appAction?.mode === "reject" ? "Reject Application" : "Request Information"}</DialogTitle></DialogHeader>
          {appAction && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{appAction.mode === "reject"
                ? `Reject ${appAction.app.name}'s freelancer application? The applicant will see the reason and can resubmit. Records are never deleted.`
                : `Ask ${appAction.app.name} for more information before approval.`}</p>
              <Textarea data-testid="app-action-note" value={appNote} onChange={(e) => setAppNote(e.target.value)} rows={3}
                placeholder={appAction.mode === "reject" ? "Rejection reason (required)…" : "What do you need? (required)…"} />
              <Button data-testid="app-action-confirm" disabled={busy || !appNote.trim()} variant={appAction.mode === "reject" ? "destructive" : "default"}
                onClick={() => act(async () => {
                  await api.post(`/root/mkt/applications/${appAction.app.user_id}/${appAction.mode === "reject" ? "reject" : "request-info"}`, appAction.mode === "reject" ? { reason: appNote } : { note: appNote });
                  setAppAction(null); loadApps();
                }, appAction.mode === "reject" ? "Application rejected" : "Info requested")}
                className="w-full rounded-full font-bold">Confirm</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* REFUND DIALOG */}
      <Dialog open={!!refundFor} onOpenChange={(o) => !o && setRefundFor(null)}>
        <DialogContent data-testid="refund-dialog">
          <DialogHeader><DialogTitle className="font-display">Refund Order</DialogTitle></DialogHeader>
          {refundFor && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Refund <b>{fmtINR(refundFor.amount)}</b> to <b>{refundFor.buyer_name}</b> for “{refundFor.item_title}”? Seller earnings for this order will be reversed.</p>
              <div className="space-y-1.5"><Label>Note (optional)</Label><Textarea data-testid="refund-note" value={refundNote} onChange={(e) => setRefundNote(e.target.value)} rows={2} /></div>
              <Button data-testid="refund-confirm" disabled={busy} variant="destructive"
                onClick={() => act(async () => { await api.post(`/root/mkt/orders/${refundFor.id}/refund`, { note: refundNote }); setRefundFor(null); setRefundNote(""); loadOrders(); loadOv(); }, "Order refunded")}
                className="w-full rounded-full font-bold">Confirm Refund</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
